import { useEffect, useMemo, useRef, useState } from 'react';
import { Leaf, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCarbonConfigForAccount } from '@/hooks/useCarbonConfig';
import type { CarbonData } from '@/hooks/useCarbonTracking';
import {
  CarbonImpactPicker,
  EMPTY_CARBON_IMPACT,
  type CarbonImpactValue,
  type CarbonImpactResolved,
} from '@/components/carbon/CarbonImpactPicker';

interface LineItemCarbonProps {
  accountId?: string;
  /** Existing carbon for this line (edit-mode hydration; arrives asynchronously). */
  value: CarbonData | null | undefined;
  onChange: (data: CarbonData | null) => void;
}

const valueToImpact = (v: CarbonData): CarbonImpactValue => ({
  emissionTableId: v.table_id,
  dimensionValues: v.dimension_values,
  quantity: v.quantity,
  quantityUnit: v.quantity_unit_code ?? '',
});

/**
 * Per-line-item carbon capture for expense / AP / AR / journal line items. Wraps
 * the shared CarbonImpactPicker so any line can attach a carbon impact — the
 * general "5 cups" case where the account has no 1:1 factor — while an account
 * that IS carbon-linked (e.g. electricity → kWh) pre-fills its table + unit and
 * enforces its pinned dimensions (passed as `lockedDimensions`).
 *
 * The persisted {@link CarbonData} is derived ENTIRELY from the picker's
 * authoritative `onResolvedChange` report (a self-consistent factor + normalized
 * quantity), so it never mixes a new factor with a stale unit and never depends
 * on the debounced live-estimate timer. Local edits set `impact` for the
 * controlled picker; async-arriving stored `value` re-hydrates until the user
 * touches the field.
 */
export function LineItemCarbon({ accountId, value, onChange }: LineItemCarbonProps) {
  const { data: carbonConfig } = useCarbonConfigForAccount(accountId);

  const [impact, setImpact] = useState<CarbonImpactValue>(() =>
    value ? valueToImpact(value) : EMPTY_CARBON_IMPACT,
  );
  const [expanded, setExpanded] = useState<boolean>(!!value);
  // Once the user edits/removes, stop auto-seeding from value/config.
  const userTouchedRef = useRef(false);
  // Once we've hydrated from a non-null stored value, don't re-seed (avoids a
  // loop with our own onChange, which flows back into `value`).
  const hydratedRef = useRef(!!value);

  // Account-pinned dimensions (deterministic factor for carbon-linked accounts).
  // The pin applies ONLY while the picker is still on the account's configured table.
  // A pin like {year:2025} is defined against that specific table's factors; if the
  // user switches to a different table, the pin's values may not exist there and would
  // wrongly exclude every non-matching factor (e.g. a 2021 factor), surfacing as a
  // spurious "fill in all required fields". Drop the pin once the table diverges.
  const lockedDimensions = useMemo(() => {
    if (!carbonConfig?.table_id || impact.emissionTableId !== carbonConfig.table_id) {
      return undefined;
    }
    const f = (carbonConfig.dimension_filter as Record<string, string> | undefined) ?? {};
    return Object.keys(f).length > 0 ? f : undefined;
  }, [carbonConfig?.table_id, carbonConfig?.dimension_filter, impact.emissionTableId]);

  // Re-hydrate when the stored value arrives asynchronously (edit mode: the carbon
  // fetch + form.reset land after this mounts). Runs once, and never over a user edit.
  useEffect(() => {
    if (userTouchedRef.current || hydratedRef.current) return;
    if (value) {
      hydratedRef.current = true;
      setImpact(valueToImpact(value));
      setExpanded(true);
    }
  }, [value]);

  // For a carbon-linked account with no stored carbon yet, pre-fill the picker
  // (table + unit) and show it — the simple "electricity → kWh" flow. Pinned dims
  // are supplied via lockedDimensions, not seeded here.
  useEffect(() => {
    if (userTouchedRef.current || hydratedRef.current || value) return;
    if (carbonConfig?.table_id && !impact.emissionTableId) {
      setImpact({
        emissionTableId: carbonConfig.table_id,
        dimensionValues: {},
        quantity: 0,
        quantityUnit: carbonConfig.multiplier_unit_code ?? '',
      });
      setExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carbonConfig?.table_id, value]);

  // Local edits: update the controlled picker value; mark touched so auto-seeding
  // stops. Does NOT emit — persistence is driven by the picker's resolved report.
  const handleChange = (next: CarbonImpactValue) => {
    userTouchedRef.current = true;
    setImpact(next);
  };

  // Authoritative persistence: the picker reports a self-consistent, synchronously
  // computed payload. We only PROPAGATE the report after the user has actually
  // interacted — before that the parent already holds the hydrated/stored carbon,
  // and the picker's factor queries are still loading (resolvedFactor undefined →
  // a transient not-ready report). Emitting on that transient would clobber stored
  // carbon and spuriously dirty the form, so it's suppressed until a real edit.
  const handleResolved = (r: CarbonImpactResolved) => {
    if (!userTouchedRef.current) return;
    if (!r.ready || r.quantityNormalized == null) {
      onChange(null);
      return;
    }
    onChange({
      table_id: r.emissionTableId,
      dimension_values: r.dimensionValues,
      quantity: r.quantity,
      quantity_unit_code: r.quantityUnit || null,
      multiplier_value: r.quantityNormalized,
      emission_factor_value: r.emissionFactor ?? undefined,
      calculated_emissions: r.calculatedKg ?? undefined,
    });
  };

  const handleRemove = () => {
    userTouchedRef.current = true;
    hydratedRef.current = true;
    setImpact(EMPTY_CARBON_IMPACT);
    setExpanded(false);
    onChange(null);
  };

  if (!expanded) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        // Full-width dashed "add area" — the button expands into the carbon
        // form, so it takes the sub-row's width like the journal modal's
        // add-entry affordance rather than floating as a small inline button.
        className="w-full border-dashed border-2 text-muted-foreground hover:text-accent-foreground hover:border-solid"
        onClick={() => setExpanded(true)}
      >
        <Leaf className="h-4 w-4 mr-2 text-releve-green" />
        Add carbon impact
      </Button>
    );
  }

  return (
    <div className="rounded-md border border-success/30 bg-success/5 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-medium text-success">
          <Leaf className="h-4 w-4 text-releve-green" />
          Carbon impact
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={handleRemove}>
          <X className="h-4 w-4 mr-1" />
          Remove
        </Button>
      </div>
      <CarbonImpactPicker
        value={impact}
        onChange={handleChange}
        onResolvedChange={handleResolved}
        lockedDimensions={lockedDimensions}
      />
    </div>
  );
}
